import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Field, Header, EmptyState } from "@/pages/Vitals";
import { Plus, ClipboardList, Trash2, Upload, ScanLine, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const newParamId = () => `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function Diagnostics() {
  const { activeId, active } = useMembers();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), type: "", parameters: [] });
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!activeId) return;
    api.get(`/diagnostics?member_id=${activeId}`).then(r => setItems(r.data));
  }, [activeId]);
  useEffect(() => {
    load();
    api.get("/diagnostics/types").then(r => setTypes(r.data.types));
  }, [load]);

  const onScan = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setScanning(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = String(reader.result).split(",")[1];
        try {
          const r = await api.post("/ocr/scan-report", { image_base64: base64, mime_type: file.type });
          setForm(f => ({ ...f, type: r.data.type || f.type, lab_name: r.data.lab_name || f.lab_name,
            date: r.data.date || f.date, parameters: r.data.parameters || [] }));
          toast.success("Report scanned successfully");
        } catch { toast.error("Could not scan report"); }
        finally { setScanning(false); }
      };
      reader.readAsDataURL(file);
    } catch { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type) { toast.error("Select a diagnostic type"); return; }
    if (!activeId) { toast.error("Select a member first"); return; }
    setLoading(true);
    try {
      const params = (form.parameters || []).map(({ _key, ...rest }) => rest);
      await api.post("/diagnostics", { ...form, parameters: params, member_id: activeId });
      toast.success("Diagnostic saved");
      setOpen(false); setForm({ date: today(), type: "", parameters: [] }); load();
    } finally { setLoading(false); }
  };

  const addParam = () => setForm(f => ({ ...f, parameters: [...(f.parameters||[]), { _key: newParamId(), name:"", value:"", unit:"", reference:"", flag:"normal" }] }));
  const setParam = (i, k, v) => setForm(f => { const p = [...f.parameters]; p[i] = { ...p[i], [k]: v }; return { ...f, parameters: p }; });
  const delParam = (i) => setForm(f => ({ ...f, parameters: f.parameters.filter((_, j) => j !== i) }));
  const del = async (id) => { await api.delete(`/diagnostics/${id}`); toast.success("Deleted"); load(); };

  const flagColor = (flag) => flag === "high" ? "text-[#C76B58] bg-[#C76B58]/10" : flag === "low" ? "text-[#5B7582] bg-[#5B7582]/10" : "text-[#5B8266] bg-[#5B8266]/10";

  return (
    <div className="space-y-6">
      <Header title="Diagnostics" subtitle={`For ${active?.name || "—"}. Track lab reports and imaging. Scan documents to auto-extract parameters.`}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-diagnostic-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]"><Plus className="w-4 h-4 mr-2" /> Add Report</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Add Diagnostic Report</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="diagnostic-form">
                <label className="flex items-center gap-3 p-4 border-2 border-dashed border-[#E5E7E4] rounded-xl bg-[#F9F8F6] cursor-pointer hover:border-[#4A6750] transition-colors">
                  {scanning ? <Loader2 className="w-5 h-5 animate-spin text-[#4A6750]" /> : <ScanLine className="w-5 h-5 text-[#4A6750]" />}
                  <span className="text-sm font-medium text-[#1C2E24]">{scanning ? "Scanning report..." : "Scan a report image to auto-fill"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onScan} disabled={scanning} data-testid="scan-report-input" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Type">
                    <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                      <SelectTrigger data-testid="diag-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Date"><Input data-testid="diag-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></Field>
                  <Field label="Lab Name"><Input data-testid="diag-lab" value={form.lab_name || ""} onChange={e => setForm({...form, lab_name: e.target.value})} /></Field>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">Parameters</p>
                    <Button type="button" variant="ghost" size="sm" data-testid="add-param-btn" onClick={addParam}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  </div>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {(form.parameters || []).map((p, i) => (
                      <div key={p._key || `idx-${i}`} className="grid grid-cols-12 gap-2">
                        <Input className="col-span-3" placeholder="Name" value={p.name} onChange={e => setParam(i,"name",e.target.value)} />
                        <Input className="col-span-2" placeholder="Value" value={p.value} onChange={e => setParam(i,"value",e.target.value)} />
                        <Input className="col-span-2" placeholder="Unit" value={p.unit || ""} onChange={e => setParam(i,"unit",e.target.value)} />
                        <Input className="col-span-3" placeholder="Reference" value={p.reference || ""} onChange={e => setParam(i,"reference",e.target.value)} />
                        <Select value={p.flag || "normal"} onValueChange={v => setParam(i,"flag",v)}>
                          <SelectTrigger className="col-span-1 px-2"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                        </Select>
                        <button type="button" onClick={() => delParam(i)} className="col-span-1 text-[#C76B58]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <Field label="Notes"><Textarea data-testid="diag-notes" rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} /></Field>
                <Button type="submit" disabled={loading} data-testid="save-diagnostic-btn" className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">Save Report</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No diagnostics yet" subtitle="Add a report manually or scan a document." />
      ) : (
        <div className="space-y-4">
          {items.map(d => (
            <Card key={d.id} className="p-5 rounded-2xl border border-[#E5E7E4]" data-testid={`diagnostic-${d.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-[#7D8F84]">{d.date}{d.lab_name ? ` · ${d.lab_name}` : ""}</p>
                  <h3 className="font-heading text-lg font-medium mt-1">{d.type}</h3>
                </div>
                <button data-testid={`del-diag-${d.id}`} onClick={() => del(d.id)} className="text-[#7D8F84] hover:text-[#C76B58] p-1.5"><Trash2 className="w-4 h-4" /></button>
              </div>
              {d.parameters?.length > 0 && (
                <Table className="mt-4">
                  <TableHeader>
                    <TableRow className="border-b border-[#E5E7E4]">
                      <TableHead className="text-xs uppercase">Parameter</TableHead>
                      <TableHead className="text-xs uppercase">Value</TableHead>
                      <TableHead className="text-xs uppercase">Reference</TableHead>
                      <TableHead className="text-xs uppercase">Flag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.parameters.map((p, i) => (
                      <TableRow key={`${p.name || "p"}-${i}`} className="border-b border-[#E5E7E4]">
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>{p.value} {p.unit}</TableCell>
                        <TableCell className="text-[#7D8F84]">{p.reference || "—"}</TableCell>
                        <TableCell><span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${flagColor(p.flag)}`}>{p.flag || "normal"}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {d.notes && <p className="mt-3 text-sm text-[#5C6D63] italic">{d.notes}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
